import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PillButton } from '../components/ui/PillButton';
import { TextInput } from '../components/ui/TextInput';
import { api, ApiError } from '../lib/api/client';
import { useAuth } from '../lib/auth/store';

export const Route = createFileRoute('/_auth/login')({
  component: LoginPage,
});

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(1, 'Required'),
});

function LoginPage() {
  const router = useRouter();
  const setSession = useAuth((s) => s.setSession);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<{ email: string; password: string }>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: { email: string; password: string }) {
    try {
      const res = await api.auth.login(values);
      setSession(res.user, { accessToken: res.accessToken, refreshToken: res.refreshToken });
      router.navigate({ to: '/dashboard' });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Login failed';
      setError('root', { message });
    }
  }

  return (
    <div>
      <p className="mono-label text-muted-slate">SIGN IN</p>
      <h1 className="font-display text-card-heading text-ink mt-3">Welcome back.</h1>
      <p className="text-body text-muted-slate mt-2">Sign in to access your dashboard.</p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 flex flex-col gap-5">
        <TextInput
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
          {...register('email')}
          error={errors.email?.message}
        />
        <TextInput
          label="Password"
          type="password"
          autoComplete="current-password"
          {...register('password')}
          error={errors.password?.message}
        />
        {errors.root?.message && (
          <p className="text-caption text-error-red">{errors.root.message}</p>
        )}
        <PillButton type="submit" loading={isSubmitting}>Sign in</PillButton>
      </form>

      <p className="mt-8 text-caption text-muted-slate">
        New here?{' '}
        <Link to="/signup" className="text-link">Request access</Link>
      </p>
    </div>
  );
}
